import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MaterialSymbol from '../common/MaterialSymbol';
import { MATERIAL_SYMBOLS_SUBSET } from '../../designSystemIcons';

// Ensure API_URL ends with /api
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

interface LandingPageProduct {
    id: number;
    title: string;
    description: string | null;
    fullDescription: string | null;
    thumbnailUrl: string | null;
    type: string;
    prices: Array<{
        id: number;
        priceType: string;
        amount: string;
        currency: string;
        isDefault: boolean;
        formattedPrice: string;
    }>;
    isFeatured: boolean;
    badgeText: string | null;
    badgeColor: string | null;
}

interface LandingPageFeature {
    icon: string;
    title: string;
    description: string;
}

interface LandingPageFaqItem {
    question: string;
    answer: string;
}

interface LandingPage {
    id: number;
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    heroImageUrl: string | null;
    logoUrl: string | null;
    layout: string;
    allowGuestCheckout: boolean;
    requireRegistration: boolean;
    features: LandingPageFeature[] | null;
    faq: LandingPageFaqItem[] | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    footerText: string | null;
    privacyPolicyUrl: string | null;
    termsUrl: string | null;
    products: LandingPageProduct[];
    companyId: number;
}

const PublicLandingPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [landingPage, setLandingPage] = useState<LandingPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

    useEffect(() => {
        const fetchLandingPage = async () => {
            try {
                setLoading(true);
                const previewToken = new URLSearchParams(window.location.search).get('preview');
                const url = previewToken
                    ? `${API_URL}/public/landing-pages/${slug}?preview=${encodeURIComponent(previewToken)}`
                    : `${API_URL}/public/landing-pages/${slug}`;
                const response = await fetch(url);

                if (!response.ok) {
                    if (response.status === 404) {
                        setError('Diese Seite existiert nicht.');
                    } else {
                        setError('Fehler beim Laden der Seite.');
                    }
                    return;
                }

                const data = await response.json();
                setLandingPage(data.landingPage);

                // Auto-select all products for single product layout
                if (data.landingPage.layout === 'single_product' && data.landingPage.products.length > 0) {
                    setSelectedProducts([data.landingPage.products[0].id]);
                }
            } catch (err) {
                console.error('Error fetching landing page:', err);
                setError('Fehler beim Laden der Seite.');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchLandingPage();
        }
    }, [slug]);

    const toggleProduct = (productId: number) => {
        setSelectedProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleCheckout = () => {
        if (selectedProducts.length === 0 || calculateTotal() > 0) return;

        // Navigate to guest checkout with selected products
        navigate(`/p/${slug}/checkout`, {
            state: {
                landingPage,
                selectedProducts,
            }
        });
    };

    const getDefaultPrice = (product: LandingPageProduct) => {
        return product.prices.find(p => p.isDefault) || product.prices[0];
    };

    const calculateTotal = () => {
        let total = 0;
        selectedProducts.forEach(productId => {
            const product = landingPage?.products.find(p => p.id === productId);
            if (product) {
                const price = getDefaultPrice(product);
                if (price) {
                    total += parseFloat(price.amount);
                }
            }
        });
        return total;
    };

    // Create dynamic theme based on landing page colors
    const theme = createTheme({
        palette: {
            primary: {
                main: landingPage?.primaryColor || '#1976d2',
            },
            secondary: {
                main: landingPage?.secondaryColor || '#dc004e',
            },
        },
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !landingPage) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Alert severity="error">{error || 'Seite nicht gefunden'}</Alert>
            </Container>
        );
    }

    const totalAmount = calculateTotal();
    const currency = landingPage.products[0]?.prices[0]?.currency || 'EUR';

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
                {/* Hero Section */}
                <Box
                    sx={{
                        background: landingPage.heroImageUrl
                            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${landingPage.heroImageUrl})`
                            : `linear-gradient(135deg, ${landingPage.primaryColor || '#1976d2'} 0%, ${landingPage.secondaryColor || '#1565c0'} 100%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        color: 'white',
                        py: 10,
                    }}
                >
                    <Container maxWidth="lg">
                        {landingPage.logoUrl && (
                            <Box sx={{ mb: 4 }}>
                                <img
                                    src={landingPage.logoUrl}
                                    alt="Logo"
                                    style={{ maxHeight: 60, maxWidth: 200 }}
                                />
                            </Box>
                        )}
                        <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                            {landingPage.title}
                        </Typography>
                        {landingPage.subtitle && (
                            <Typography variant="h5" sx={{ opacity: 0.9, mb: 3 }}>
                                {landingPage.subtitle}
                            </Typography>
                        )}
                        {landingPage.description && (
                            <Typography variant="body1" sx={{ maxWidth: 700, opacity: 0.9 }}>
                                {landingPage.description}
                            </Typography>
                        )}
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ py: 6 }}>
                    <Grid container spacing={4}>
                        {/* Products */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography variant="h4" gutterBottom>
                                {landingPage.layout === 'single_product' ? 'Kurs' : 'Verfügbare Kurse'}
                            </Typography>

                            <Grid container spacing={3}>
                                {landingPage.products.map((product) => {
                                    const price = getDefaultPrice(product);
                                    const isSelected = selectedProducts.includes(product.id);

                                    return (
                                        <Grid size={{ xs: 12, sm: landingPage.layout === 'single_product' ? 12 : 6 }} key={product.id}>
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    cursor: 'pointer',
                                                    border: isSelected ? '2px solid' : '1px solid',
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                    },
                                                }}
                                                onClick={() => toggleProduct(product.id)}
                                            >
                                                {product.thumbnailUrl && (
                                                    <CardMedia
                                                        component="img"
                                                        height={180}
                                                        image={product.thumbnailUrl}
                                                        alt={product.title}
                                                    />
                                                )}
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Typography variant="h6" component="h3">
                                                            {product.title}
                                                        </Typography>
                                                        {product.badgeText && (
                                                            <Chip
                                                                label={product.badgeText}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: product.badgeColor || 'primary.main',
                                                                    color: 'white',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {product.description && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                            {product.description}
                                                        </Typography>
                                                    )}

                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="h5" color="primary" fontWeight="bold">
                                                            {price?.formattedPrice || 'Preis auf Anfrage'}
                                                        </Typography>
                                                        {isSelected && (
                                                            <CheckCircleIcon color="primary" />
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            {/* Features Section */}
                            {landingPage.features && landingPage.features.length > 0 && (
                                <Box sx={{ mt: 6 }}>
                                    <Typography variant="h4" gutterBottom>
                                        Was Sie erwartet
                                    </Typography>
                                    <Grid container spacing={3}>
                                        {landingPage.features.map((feature, index) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                                <Paper sx={{ p: 3, height: '100%' }}>
                                                    {feature.icon && MATERIAL_SYMBOLS_SUBSET.includes(feature.icon) && (
                                                        <MaterialSymbol
                                                            name={feature.icon}
                                                            size={32}
                                                            style={{
                                                                color: theme.palette.primary.main,
                                                                marginBottom: 8,
                                                                display: 'block',
                                                            }}
                                                        />
                                                    )}
                                                    <Typography variant="h6" gutterBottom>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {feature.description}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* FAQ Section */}
                            {landingPage.faq && landingPage.faq.length > 0 && (
                                <Box sx={{ mt: 6 }}>
                                    <Typography variant="h4" gutterBottom>
                                        Häufige Fragen
                                    </Typography>
                                    {landingPage.faq.map((item, index) => (
                                        <Accordion key={index}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography fontWeight="medium">{item.question}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography color="text.secondary">{item.answer}</Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            )}
                        </Grid>

                        {/* Checkout Sidebar */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper sx={{ p: 3, position: 'sticky', top: 24 }}>
                                <Typography variant="h6" gutterBottom>
                                    Ihre Auswahl
                                </Typography>

                                {selectedProducts.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                        Wählen Sie einen oder mehrere Kurse aus.
                                    </Typography>
                                ) : (
                                    <>
                                        <List dense>
                                            {selectedProducts.map(productId => {
                                                const product = landingPage.products.find(p => p.id === productId);
                                                const price = product ? getDefaultPrice(product) : null;
                                                return product ? (
                                                    <ListItem key={productId}>
                                                        <ListItemIcon>
                                                            <CheckCircleIcon color="primary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={product.title}
                                                            secondary={price?.formattedPrice}
                                                        />
                                                    </ListItem>
                                                ) : null;
                                            })}
                                        </List>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                            <Typography variant="h6">Gesamt</Typography>
                                            <Typography variant="h6" color="primary">
                                                {new Intl.NumberFormat('de-DE', {
                                                    style: 'currency',
                                                    currency: currency,
                                                }).format(totalAmount)}
                                            </Typography>
                                        </Box>
                                    </>
                                )}

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    startIcon={<ShoppingCartIcon />}
                                    onClick={handleCheckout}
                                    disabled={selectedProducts.length === 0 || totalAmount > 0}
                                >
                                    Kostenlos starten
                                </Button>

                                {totalAmount > 0 ? (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        Für dieses Angebot ist derzeit kein Bezahlvorgang möglich. Bitte wenden Sie sich an den Anbieter dieser Seite.
                                    </Alert>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                                        Sie erhalten nach dem Kauf automatisch Ihren Zugang.
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>

                {/* Footer */}
                <Box sx={{ bgcolor: 'grey.100', py: 4, mt: 6 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {landingPage.footerText || 'Online Academy'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {landingPage.privacyPolicyUrl && (
                                    <Typography
                                        component="a"
                                        href={landingPage.privacyPolicyUrl}
                                        target="_blank"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        Datenschutz
                                    </Typography>
                                )}
                                {landingPage.termsUrl && (
                                    <Typography
                                        component="a"
                                        href={landingPage.termsUrl}
                                        target="_blank"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        AGB
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default PublicLandingPage;
